// Phase 3 (api-phase3.md): words and colours for the AI agent, shared by the console, setup, QA and the AI report.
// Anything the AI produced is violet; verdict chips take the severity colour of what the verdict means.

import type { AgentStep, AgentToolCall, EventState, Instruction, Verdict, Watch } from '../api.ts';
import { camShort, human, type SevCls } from './util.ts';

export const VERDICT_LABEL: Record<Verdict, string> = {
  clear: 'Clear',
  authorized: 'Authorized',
  monitor: 'Monitor',
  suspicious: 'Suspicious',
  criminal_life_safety: 'Criminal / life safety',
};

export function verdictLabel(v: string | null | undefined): string {
  if (!v) return 'No verdict';
  return VERDICT_LABEL[v as Verdict] ?? human(v);
}

/** Severity colour class for a verdict chip. */
export const VERDICT_SEV: Record<Verdict, SevCls> = {
  clear: 'low',
  authorized: 'low',
  monitor: 'med',
  suspicious: 'high',
  criminal_life_safety: 'crit',
};

export function verdictCls(v: string | null | undefined): SevCls {
  return (v && VERDICT_SEV[v as Verdict]) || 'info';
}

export function pct(x: number | null | undefined, digits = 0): string {
  if (x === null || x === undefined || !Number.isFinite(x)) return '—';
  return `${(x * 100).toFixed(digits)}%`;
}

export function money(x: number | null | undefined): string {
  if (x === null || x === undefined || !Number.isFinite(x)) return '—';
  if (x === 0) return '$0';
  return x < 0.01 ? `$${x.toFixed(4)}` : `$${x.toFixed(2)}`;
}

/** Event states in plain words. Phase 3 adds the agent's two. */
export function stateLabel(s: EventState | string | null | undefined): string {
  if (s === 'agent_working') return 'AI triaging';
  if (s === 'settling') return 'Auto-resolved';
  return human(s);
}

/** Long form for tooltips and read-only bars. */
export function stateHint(s: EventState | string | null | undefined): string | null {
  if (s === 'agent_working') return 'The AI agent is triaging this event. It goes to an operator if it needs a human.';
  if (s === 'settling') return 'Auto-resolved by the AI agent: it closes on its own unless another alarm arrives.';
  return null;
}

export const AI_STATES: readonly string[] = ['agent_working', 'settling'];

export const LEVEL_NAMES = ['Shadow', 'Triage', 'Deter', 'Co-pilot', 'Resolve'];

export function levelLabel(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `Level ${n} · ${LEVEL_NAMES[n] ?? '?'}`;
}

export const RUN_STATUS_LABEL: Record<string, string> = {
  running: 'Working',
  waiting: 'Waiting',
  paused: 'Paused',
  handed_off: 'Handed off',
  completed: 'Completed',
  failed: 'Failed',
  absent: 'Absent',
};

export function runStatusLabel(s: string | null | undefined): string {
  return (s && RUN_STATUS_LABEL[s]) ?? human(s);
}

/* ---------- trace ---------- */

const TOOL_WORDS: Record<string, string> = {
  get_event: 'Read the event',
  site_context: 'Read the site context',
  site_history: 'Checked the site history',
  get_alarm_media: 'Looked at the alarm snapshots',
  get_frames: 'Pulled frames from a camera',
  attach_note: 'Added a note',
  record_assessment: 'Recorded its assessment',
  propose_disposition: 'Proposed a disposition',
  heartbeat_extend: 'Asked for more time',
  escalate: 'Handed the event to an operator',
  resolve_event: 'Auto-resolved the event',
  // Phase 4 (api-phase4.md "New agent tools")
  set_watch: 'Set a watch',
  cancel_watch: 'Cancelled a watch',
  get_watch_hit: 'Looked at a watch hit',
  confirm_watch_hit: 'Checked a watch hit',
  reply_to_operator: 'Replied to the operator',
  draft_dispatch: 'Drafted the dispatch description',
  draft_report: 'Drafted the customer report',
  complete_mission_step: 'Checked off a mission step',
  // Phase 5 (api-phase5.md "Agent deterrence")
  get_devices: "Checked the site's speakers and deterrents",
  talk_down: 'Played a talk-down',
  trigger_relay: 'Pulsed a deterrent',
  notify_customer: 'Notified the customer',
};

export function toolWords(name: string): string {
  return TOOL_WORDS[name] ?? human(name);
}

/** What the would-have means for a shadow decision. */
const TOOL_WOULD: Record<string, string> = {
  escalate: 'Would have handed the event to an operator',
  resolve_event: 'Would have auto-resolved the event',
  attach_note: 'Would have added a note to the event log',
  talk_down: 'Would have played a talk-down',
  trigger_relay: 'Would have pulsed a deterrent',
  notify_customer: 'Would have notified the customer',
};

export function toolWouldWords(name: string): string {
  return TOOL_WOULD[name] ?? `Would have: ${toolWords(name).toLowerCase()}`;
}

/** Words for one call, reading its arguments where the name alone is not enough (resolve_event kind end_to_end). */
export function callWords(c: Pick<AgentToolCall, 'tool' | 'args' | 'decision'>): string {
  const e2e = c.tool === 'resolve_event' && c.args?.kind === 'end_to_end';
  if (c.decision === 'shadow') return e2e ? 'Would have resolved the event end to end' : toolWouldWords(c.tool);
  return e2e ? 'Resolved the event end to end' : toolWords(c.tool);
}

/** Names for device and message ids (console/devices.ts), so device calls read "Leave the property" on Fence East. */
export interface DeviceWords {
  device: (id: string) => string | null;
  zone: (id: string) => string | null;
  message: (id: string) => { name: string; text: string } | null;
}

function list(xs: string[]): string {
  return xs.length <= 1 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
}

/**
 * The agent's device or notification request in plain words for an approval: "Play 'Leave the property' on Fence East",
 * "Pulse Strobe · Fence East for 10 s", "Text the customer (activity)". null when there is nothing better than the
 * server's summary.
 */
export function deviceAsk(tool: string, args: Record<string, unknown> | null | undefined, names?: DeviceWords | null): string | null {
  const a = args ?? {};
  if (tool === 'talk_down') {
    const ids = Array.isArray(a.speakerIds) ? a.speakerIds.filter((x): x is string => typeof x === 'string') : [];
    const where = ids.map((id) => names?.zone(id) ?? names?.device(id) ?? id);
    const m = typeof a.messageId === 'string' ? names?.message(a.messageId) : null;
    return `AI asks to play ${m ? `“${m.name}”` : 'a talk-down'}${where.length ? ` on ${list(where)}` : ''}`;
  }
  if (tool === 'trigger_relay') {
    const id = typeof a.deviceId === 'string' ? a.deviceId : null;
    const sec = n(a.seconds);
    return `AI asks to pulse ${id ? (names?.device(id) ?? 'a deterrent') : 'a deterrent'}${sec !== null ? ` for ${sec} s` : ''}`;
  }
  if (tool === 'notify_customer') {
    const t = s(a.templateCode);
    return `AI asks to notify the customer${t ? ` (${human(t).toLowerCase()})` : ''}${s(a.summary) ? `: “${s(a.summary)}”` : ''}`;
  }
  if (tool === 'resolve_event' && a.kind === 'end_to_end') return `AI asks to resolve the event end to end${s(a.dispositionCode) ? ` as ${s(a.dispositionCode)}` : ''}`;
  return null;
}

/** "AI asks to run talk_down" (the server's generic summary) into words when no arguments are at hand. */
export function askWords(summary: string, tool: string): string {
  const m = /^AI asks to run (\w+)$/.exec(summary.trim());
  if (!m) return summary;
  return deviceAsk(tool, null, null) ?? `AI asks to: ${toolWords(tool).toLowerCase()}`;
}

const REASON_WORDS: Record<string, string> = {
  hard_blocked: 'never allowed for the AI agent; a human decides it',
  unknown_tool: 'there is no tool by that name',
  paused: 'the agent is paused',
  budget: 'its budget for this event is used up',
  rate_limit: 'rate-limited at this site',
  human_only: 'this event must be decided by a human',
  human_control: 'an operator has this event',
  below_threshold: "confidence is below the site's auto-resolve threshold",
  unreviewed_alarm: 'there are alarms it had not reviewed',
  verdict: 'only clear or authorized events can be auto-resolved',
  authorized_policy: 'this site escalates authorized activity instead of resolving it',
  level: "the site's autonomy level does not allow it yet",
  off: 'turned off at this site',
  extension_limit: 'no more time extensions left',
  not_agent_working: 'the event is no longer with the agent',
  not_available: 'not available yet',
  // Phase 5
  authorized_possible: 'authorized activity may be on site now',
  policy: 'this site asks for an OK before this tool',
  pulse_only: 'the agent may only pulse a relay',
  device_threshold: "confidence is below the site's device threshold",
};

export function reasonWords(r: string | null | undefined, tool?: string | null): string | null {
  if (!r) return null;
  // Device and notification tools compare against the device threshold, not the auto-resolve one.
  if (r === 'below_threshold' && tool && ['talk_down', 'trigger_relay', 'notify_customer'].includes(tool)) return REASON_WORDS.device_threshold ?? null;
  return REASON_WORDS[r] ?? human(r).toLowerCase();
}

const TRIGGER_WORDS: Record<string, string> = {
  event_created: 'New event',
  alarm_attached: 'New alarm on the event',
  pause: 'Paused',
  resume: 'Resumed',
  escalated: 'Escalated',
  event_closed: 'Event closed',
  operator_instruction: 'Instruction from the operator',
  watch_hit: 'Watch hit',
  approval_decided: 'Approval answered',
};

export function triggerWords(t: string | null | undefined): string {
  if (!t) return 'Turn';
  return TRIGGER_WORDS[t] ?? human(t);
}

export const STEP_KIND_LABEL: Record<string, string> = {
  observe: 'Observe',
  assess: 'Assess',
  act: 'Act',
  evaluate: 'Evaluate',
  handoff: 'Handoff',
  message: 'Reply',
};

function s(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}
function n(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
function arr(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

/** A short line for what a tool call found or did. */
export function toolResultLine(c: AgentToolCall, names?: DeviceWords | null): string | null {
  const a = c.args ?? {};
  const r = c.result ?? {};
  if (c.error) return c.error;
  if (s(r.error)) return s(r.error);
  switch (c.tool) {
    case 'record_assessment': {
      const conf = n(a.confidence);
      const v = s(a.verdict);
      return [v ? `${verdictLabel(v)}${conf !== null ? ` ${pct(conf)}` : ''}` : null, s(a.summary)].filter(Boolean).join(' · ') || null;
    }
    case 'resolve_event': {
      const conf = n(a.confidence);
      const v = s(a.verdict);
      const parts = [v ? `${verdictLabel(v)}${conf !== null ? ` ${pct(conf)}` : ''}` : null, s(a.code) ?? s(a.dispositionCode), s(a.notes)];
      const st = s(r.state);
      if (st) parts.push(st === 'settling' ? 'closing unless another alarm arrives' : stateLabel(st).toLowerCase());
      if (a.kind === 'end_to_end') parts.push('end to end · every one goes to QA');
      return parts.filter(Boolean).join(' · ') || null;
    }
    case 'escalate': {
      const parts = [s(a.priority) ? `${human(s(a.priority))} priority` : null, s(a.reason), s(r.note)];
      return parts.filter(Boolean).join(' · ') || null;
    }
    case 'get_alarm_media': {
      const f = arr(r.frames).length;
      const cl = arr(r.clips).length;
      return c.decision === 'allow' ? `${f} snapshot${f === 1 ? '' : 's'}${cl ? `, ${cl} clip${cl === 1 ? '' : 's'}` : ''}` : null;
    }
    case 'get_frames': {
      const f = arr(r.frames).length;
      return s(r.note) ?? (c.decision === 'allow' ? `${f} frame${f === 1 ? '' : 's'}` : null);
    }
    case 'site_context': {
      const lt = s(r.localTime);
      const act = arr(r.authorizedActivity).filter((x) => typeof x === 'object' && x !== null && (x as Record<string, unknown>).inEffectNow === true);
      const labels = act.map((x) => s((x as Record<string, unknown>).label)).filter(Boolean);
      return [lt ? `Site time ${lt}` : null, labels.length ? `authorized now: ${labels.join(', ')}` : 'no authorized activity now'].filter(Boolean).join(' · ');
    }
    case 'site_history': {
      const by = r.last90Days;
      if (by && typeof by === 'object') {
        const parts = Object.entries(by as Record<string, unknown>).map(([k, v]) => `${n(v) ?? 0} ${human(k).toLowerCase()}`);
        return parts.length ? `Last 90 days: ${parts.join(', ')}` : 'No closed events in 90 days';
      }
      return null;
    }
    case 'get_event': {
      const al = arr(r.alarms).length;
      return al ? `${al} alarm${al === 1 ? '' : 's'}` : null;
    }
    case 'propose_disposition':
      return [s(r.label) ?? s(a.code), s(a.notes)].filter(Boolean).join(' · ') || null;
    case 'attach_note':
      return s(a.text);
    case 'heartbeat_extend': {
      const sec = n(r.extendedBySeconds) ?? n(a.seconds);
      return [sec !== null ? `+${sec} s` : null, s(a.reason)].filter(Boolean).join(' · ') || null;
    }
    case 'set_watch': {
      const cond = s(a.condition);
      const mins = n(a.minutes);
      return [cond ? conditionLabel(cond) : null, s(a.description), mins !== null ? `${mins} min` : null].filter(Boolean).join(' · ') || null;
    }
    case 'confirm_watch_hit':
      return [a.confirmed === true ? 'confirmed: raised the event' : a.confirmed === false ? 'not it: still watching' : null, s(a.note)].filter(Boolean).join(' · ') || null;
    case 'reply_to_operator':
      return s(a.text);
    case 'draft_dispatch':
      return s(a.summary);
    case 'draft_report':
      return s(a.narrative);
    case 'get_devices': {
      const sp = arr(r.speakers).length;
      const rl = arr(r.relays).length;
      const ms = arr(r.messages).length;
      return c.decision === 'allow' ? `${sp} speaker${sp === 1 ? '' : 's'}, ${rl} relay${rl === 1 ? '' : 's'}, ${ms} message${ms === 1 ? '' : 's'} it may play` : null;
    }
    case 'talk_down': {
      const ids = arr(a.speakerIds).filter((x): x is string => typeof x === 'string');
      const where = ids.map((id) => names?.zone(id) ?? names?.device(id) ?? id);
      const m = typeof a.messageId === 'string' ? names?.message(a.messageId) : null;
      const text = s(r.text) ?? m?.text ?? null;
      const parts = [m ? `“${m.name}”${where.length ? ` on ${list(where)}` : ''}` : where.length ? `on ${list(where)}` : null, text && (!m || text !== m.name) ? `“${text}”` : null, s(a.reason)];
      if (c.decision === 'ask' && c.status === 'pending') parts.push('waiting for an OK');
      return parts.filter(Boolean).join(' · ') || null;
    }
    case 'trigger_relay': {
      const id = s(a.deviceId);
      const sec = n(r.seconds) ?? n(a.seconds);
      const fn = s(r.function);
      const parts = [`${id ? (names?.device(id) ?? (fn ? human(fn) : 'relay')) : 'relay'}${sec !== null ? ` · pulse ${sec} s` : ''}`, s(a.reason)];
      if (c.decision === 'ask' && c.status === 'pending') parts.push('waiting for an OK');
      return parts.filter(Boolean).join(' · ') || null;
    }
    case 'notify_customer': {
      const sent = arr(r.notifications).length;
      return [s(a.templateCode) ? human(s(a.templateCode)) : null, s(a.summary) ? `“${s(a.summary)}”` : null, sent ? `${sent} sent` : null].filter(Boolean).join(' · ') || null;
    }
    case 'complete_mission_step':
      return [s(r.title) ?? s(a.stepId), s(a.note), c.decision === 'ask' && c.status === 'pending' ? 'waiting for an OK' : null].filter(Boolean).join(' · ') || null;
    default:
      return null;
  }
}

/** One trace row. `turn` is set on the first row of a turn (oldest-first): the divider label. */
export type TraceRow = ({ k: 'step'; at: number; step: AgentStep } | { k: 'tool'; at: number; call: AgentToolCall }) & { turn: string | null };

/**
 * Steps and tool calls merged in time order (oldest first), with turn boundaries. The runtime starts every turn by
 * fetching the site context, then loops model → tools; so a turn starts at a site_context call the model did not ask
 * for (the step before it does not list it), or at a step whose trigger differs from the previous step's.
 */
export function mergeTrace(steps: AgentStep[], calls: AgentToolCall[]): TraceRow[] {
  const base: TraceRow[] = [
    ...steps.slice().sort((a, b) => a.seq - b.seq).map((step) => ({ k: 'step' as const, at: Date.parse(step.at) || 0, step, turn: null })),
    ...calls.map((call) => ({ k: 'tool' as const, at: Date.parse(call.at) || 0, call, turn: null })),
  ];
  // Stable: equal times keep steps before the tool calls they asked for.
  const rows = base
    .map((r, i) => ({ r, i }))
    .sort((a, b) => a.r.at - b.r.at || a.i - b.i)
    .map((x) => x.r);
  let lastTrigger: string | null | undefined;
  let open: TraceRow | null = null; // the row that opened the current turn, until its first step names the trigger
  let prev: TraceRow | null = null;
  for (const r of rows) {
    if (r.k === 'tool' && r.call.tool === 'site_context') {
      const asked = prev?.k === 'step' && Array.isArray(prev.step.output?.toolCalls) && prev.step.output.toolCalls.includes('site_context');
      if (!asked) {
        r.turn = 'New signal';
        open = r;
      }
    } else if (r.k === 'step') {
      const changed = lastTrigger === undefined || r.step.trigger !== lastTrigger;
      lastTrigger = r.step.trigger;
      if (open) {
        open.turn = triggerWords(r.step.trigger);
        open = null;
      } else if (changed) r.turn = triggerWords(r.step.trigger);
    }
    prev = r;
  }
  return rows;
}

/** What the agent is doing now, in one line (the strip's "Now"). */
export function nowLine(
  p: { status: string; paused: boolean; pausedScope: string | null; steps: AgentStep[]; toolCalls: AgentToolCall[]; shadow: boolean; watches?: Watch[]; instructions?: Instruction[] },
  evState?: string,
): string {
  if (evState === 'settling' && !p.paused) return 'Auto-resolved. The event closes on its own unless another alarm arrives.';
  if (p.paused) return p.pausedScope === 'global' ? 'Paused for all sites. Nothing is being acted on.' : p.pausedScope === 'site' ? 'Paused for this site. Nothing is being acted on.' : 'Paused on this event. Nothing is being acted on.';
  const pendingIns = p.instructions?.find((i) => i.status === 'pending' || i.status === 'working');
  if (pendingIns) return `Working on: ${pendingIns.text}`;
  const live = (p.watches ?? []).filter(watchLive);
  if (live.some((w) => w.status === 'confirming')) return 'Checking a possible watch hit on the frame.';
  if (live.length) return watchesLine(live);
  if (p.status === 'handed_off') return 'Handed off to an operator with its findings.';
  if (p.status === 'completed') return 'Done with this event.';
  if (p.status === 'failed') return 'The agent run failed. Handle the event as usual.';
  if (p.status === 'absent') return 'The agent did not run on this event.';
  const lastCall = p.toolCalls.slice().sort((a, b) => (Date.parse(b.at) || 0) - (Date.parse(a.at) || 0))[0];
  const lastStep = p.steps.slice().sort((a, b) => b.seq - a.seq)[0];
  if (lastStep && lastCall && (Date.parse(lastStep.at) || 0) > (Date.parse(lastCall.at) || 0) && lastStep.rationale) return lastStep.rationale;
  if (lastCall) return callWords(lastCall);
  if (lastStep?.rationale) return lastStep.rationale;
  return p.status === 'waiting' ? 'Waiting for the next alarm or signal.' : 'Starting on the event.';
}

/* ---------- Phase 4: watches and instructions (api-phase4.md) ---------- */

/** Short words for a watch condition ("police", "any person"); the API also sends conditionLabel. */
const CONDITION_WORDS: Record<string, string> = {
  police_arrival: 'police',
  subject_return: 'the subject coming back',
  person_any: 'any person',
  vehicle_any: 'any vehicle',
  gate_state: 'the gate',
  custom: 'a custom condition',
};

export function conditionLabel(c: string | null | undefined, description?: string | null): string {
  if (!c) return 'something';
  if (c === 'custom' && description) return description;
  return CONDITION_WORDS[c] ?? human(c).toLowerCase();
}

/** Park drawer options (prototype drawerHTML 'park': what the AI watch raises on). */
export const CONDITION_OPTIONS: { value: string; label: string; sub: string }[] = [
  { value: 'police_arrival', label: 'Police arrival', sub: 'Marked unit, light bar, or uniformed officers' },
  { value: 'subject_return', label: 'Subject return', sub: 'The same people come back into view' },
  { value: 'person_any', label: 'Any person', sub: 'On the cameras below' },
  { value: 'vehicle_any', label: 'Any vehicle', sub: 'On the cameras below' },
  { value: 'gate_state', label: 'Gate state', sub: 'The gate opens or closes' },
  { value: 'custom', label: 'Custom', sub: 'Describe what to look for' },
];

/** Quick instructions under the command box (prototype agentPanelHTML `quick`). */
export const QUICK_INSTRUCTIONS = ['Watch for police and raise me', 'Watch for the subject coming back', 'Draft dispatch', 'Draft customer report', 'What do you see now?'];

/** The watch is still looking (active) or waiting for the agent's check (confirming). */
export function watchLive(w: { status: string }): boolean {
  return w.status === 'active' || w.status === 'confirming';
}

/** "Cam 01, 02" from "Cam 01 · Main Gate", "Cam 02 · Showroom Front". */
export function camList(names: string[]): string {
  const short = names.map((n) => camShort(n));
  if (short.length > 1 && short.every((x) => /^Cam \d+$/.test(x))) return `Cam ${short.map((x) => x.slice(4)).join(', ')}`;
  return short.join(', ');
}

/** What a watch looks for, in words: "police", "the gate (opens)", the custom description. */
export function watchWhat(w: Pick<Watch, 'condition' | 'conditionLabel' | 'description'>): string {
  if (w.condition === 'custom' && w.description) return w.description;
  return w.conditionLabel || conditionLabel(w.condition);
}

/** "Watching Cam 01, 02 for police and any person" for the agent's Now line. */
export function watchesLine(ws: Watch[]): string {
  const byCams = new Map<string, string[]>();
  for (const w of ws) {
    const k = camList(w.cameraNames.length ? w.cameraNames : w.cameraIds);
    byCams.set(k, [...(byCams.get(k) ?? []), watchWhat(w)]);
  }
  return `Watching ${[...byCams.entries()].map(([cams, what]) => `${cams} for ${[...new Set(what)].join(' and ')}`).join('; ')}`;
}

export const INSTRUCTION_STATUS_LABEL: Record<string, string> = {
  pending: 'sent',
  working: 'working',
  answered: 'answered',
  failed: 'failed',
  cancelled: 'cancelled',
};

export const APPROVAL_STATUS_LABEL: Record<string, string> = {
  pending: 'waiting',
  approved: 'approved',
  denied: 'denied',
  expired: 'expired (counts as a denial)',
  cancelled: 'cancelled (agent paused)',
};

/**
 * Did this watch hit raise the event? The hit itself does not say (its endedReason stays null), so read the event log:
 * a "raised" line just after the hit. Also returns the hit's log line ("… the AI did not confirm in time, raised to be safe").
 */
export function hitOutcome(log: { kind: string; at: string; text: string }[], w: Pick<Watch, 'hitAt' | 'endedReason'>): { raised: boolean; line: string | null } {
  const t = Date.parse(w.hitAt ?? '');
  if (Number.isNaN(t)) return { raised: w.endedReason === 'raised', line: null };
  const near = (l: { at: string }) => {
    const x = Date.parse(l.at);
    return x >= t - 2000 && x - t < 120_000;
  };
  const hit = log.find((l) => l.kind === 'watch_hit' && near(l));
  return { raised: w.endedReason === 'raised' || log.some((l) => l.kind === 'raised' && near(l)), line: hit?.text ?? null };
}
